import { OfficeDocument } from "@gettactic/api";
import { PaginatedResult } from "@gettactic/api/src/endpoints/response/paginated_result";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../providers/TacticHooksProvider";

export const OFFICE_DOCUMENTS_QUERY_KEY = "useOfficeDocumentsQuery";

const PAGE_SIZE = 20;

export const useOfficeDocumentsQuery = (officeId: string) => {
  const { apiClient } = useTacticHooks();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery<PaginatedResult<OfficeDocument> | null>({
    queryKey: [OFFICE_DOCUMENTS_QUERY_KEY, officeId],
    queryFn: async ({ pageParam: cursor }) => {
      const res = await apiClient.organizations.officeDocumentsList(
        officeId,
        {
          cursor: cursor as string | undefined,
          limit: PAGE_SIZE
        }
      );
      return res.result;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      (lastPage?.elements.length ?? 0) < PAGE_SIZE
        ? undefined
        : (lastPage?.cursor ?? undefined),
    enabled: !!officeId
  });

  const allDocuments = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page) => page?.elements ?? []),
    [data]
  );

  return {
    allDocuments,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading
  };
};
